import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Vendor Privileged Access Management (VPAM)',
    metaDescription:
      'Invited, approved, time-boxed, recorded: privileged access for vendors and third parties without VPN accounts. Self-hosted and air-gapped deployments included.',
    heroEyebrow: 'Monopam · Vendor Privileged Access',
    heroTitle: ['Vendors get a window.', 'Not a network.'],
    heroBody:
      'Third-party engineers, integrators, and support teams get exactly one thing: a time-boxed, recorded, approval-gated session to the systems they maintain. No VPN account, no shared login, no standing credentials waiting to be phished.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'Approval-gated invitations',
      'Hard expiry, enforced twice',
      'Every session recorded',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'The full vendor lifecycle, governed.',
    pillarsBody:
      'Most vendor breaches start with an account nobody remembers creating. Monopam replaces the VPN-account workflow with a lifecycle: invite, approve, connect, expire, review. It runs self-hosted and air-gapped, so OT sites and regulated networks get the same control without a SaaS dependency.',
    pillars: [
      {
        title: 'Invitations, not accounts',
        description:
          'A vendor user starts as an invitation request that passes an approval flow before any identity exists. Once approved, enrollment is a passwordless magic link: no password to share, intercept, or reuse.',
        bullets: [
          'Approval flow before the invitation is sent',
          'Passwordless magic-link enrollment',
          'Per-vendor authorized email domains',
          'MFA enforcement per invitation',
        ],
      },
      {
        title: 'Time-boxed by design',
        description:
          'Every external account carries an expiry. It is enforced in the PAM layer and mirrored into the identity layer, so a bypass attempt hits a second wall. Inactivity disables accounts that stop being used.',
        bullets: [
          'Hard expiry on every external account',
          'Expiry mirrored to the identity platform',
          'Automatic disable after inactivity',
          'Contract window per vendor organization',
        ],
      },
      {
        title: 'Sessions watched end to end',
        description:
          'Vendor connections can require just-in-time approval before they start, are forced through session recording, and notify the sponsor the moment the vendor connects.',
        bullets: [
          'Just-in-time connection approval per vendor',
          'Forced session recording for external users',
          'Sponsor notified on every connection',
          'IP and network restrictions per vendor',
        ],
      },
      {
        title: 'Governance built in',
        description:
          'Vendor organizations are first-class objects: delegated vendor admins manage their own team through a portal, access reviews attest who still needs access, and every action lands in the audit trail.',
        bullets: [
          'Vendor portal with delegated team management',
          'Periodic access reviews with disable-on-reject',
          'Per-vendor audit trail and reporting',
          'Invitations from the portal still pass your approval',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Invite. Connect. Expire.',
    steps: [
      {
        title: 'Invite the vendor',
        description:
          'Request an invitation with a vendor organization, an expiry, and an MFA requirement. Your approvers confirm it; the vendor enrolls through a magic link.',
      },
      {
        title: 'Broker the session',
        description:
          'The vendor connects through the Monopam gateway: approval-gated if you require it, recorded always, with the sponsor notified in real time.',
      },
      {
        title: 'Expire and review',
        description:
          'Access ends when the window ends, at both the PAM and identity layers. Access reviews confirm who still needs access; a rejection disables the account.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'External & partner access',
        description: 'The broader use case: federation, contractors, delegated admin.',
        href: '/solutions/external-partners',
      },
      {
        label: 'OT vendor access',
        description: 'The guide: secure remote access for plants and OT networks.',
        href: '/learn/ot-vendor-access',
      },
      {
        label: 'NIS2 compliance',
        description: 'Supply-chain access mapped to Article 21 measures.',
        href: '/compliance/nis2',
      },
    ],
  },
  tr: {
    metaTitle: 'Tedarikçi Ayrıcalıklı Erişim Yönetimi (VPAM)',
    metaDescription:
      'Davetli, onaylı, süreli, kayıtlı: tedarikçiler ve üçüncü taraflar için VPN hesabı olmadan ayrıcalıklı erişim. Kendi altyapınızda ve kapalı ağda çalışır.',
    heroEyebrow: 'Monopam · Tedarikçi Ayrıcalıklı Erişimi',
    heroTitle: ['Tedarikçiye pencere açılır.', 'Ağ açılmaz.'],
    heroBody:
      'Üçüncü taraf mühendisler, entegratörler ve destek ekipleri tek bir şey alır: bakımını yaptıkları sistemlere süreli, kayıtlı ve onaya bağlı bir oturum. VPN hesabı yok, paylaşılan giriş yok, oltalanmayı bekleyen kalıcı kimlik bilgisi yok.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Onay kapılı davetler',
      'İki katmanda kesin süre dolumu',
      'Her oturum kayıtlı',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Tedarikçi yaşam döngüsünün tamamı, yönetim altında.',
    pillarsBody:
      'Çoğu tedarikçi ihlali, kimin açtığını kimsenin hatırlamadığı bir hesapla başlar. Monopam, VPN hesabı iş akışının yerine bir yaşam döngüsü koyar: davet et, onayla, bağlan, süresi dolsun, gözden geçir. Kendi altyapınızda ve kapalı ağda çalışır; OT sahaları ve regülasyona tabi ağlar aynı kontrolü SaaS bağımlılığı olmadan alır.',
    pillars: [
      {
        title: 'Hesap değil, davet',
        description:
          'Tedarikçi kullanıcısı, daha ortada bir kimlik yokken onay akışından geçen bir davet talebi olarak başlar. Onaylandığında kayıt parolasız magic link ile yapılır: paylaşılacak, ele geçirilecek veya yeniden kullanılacak parola yoktur.',
        bullets: [
          'Davet gönderilmeden önce onay akışı',
          'Parolasız magic link ile kayıt',
          'Tedarikçi bazında yetkili e-posta alan adları',
          'Davet bazında MFA zorunluluğu',
        ],
      },
      {
        title: 'Tasarım gereği süreli',
        description:
          'Her dış hesap bir son kullanma tarihi taşır. Süre PAM katmanında uygulanır ve kimlik katmanına aynalanır; atlatma girişimi ikinci bir duvara çarpar. Kullanılmayan hesaplar hareketsizlikle devre dışı kalır.',
        bullets: [
          'Her dış hesapta kesin süre dolumu',
          'Sürenin kimlik platformuna aynalanması',
          'Hareketsizlikte otomatik devre dışı bırakma',
          'Tedarikçi kuruluşu bazında sözleşme penceresi',
        ],
      },
      {
        title: 'Uçtan uca izlenen oturumlar',
        description:
          'Tedarikçi bağlantıları başlamadan önce tam zamanında onay isteyebilir, her zaman oturum kaydından geçer ve tedarikçi bağlandığı anda sponsoru bilgilendirir.',
        bullets: [
          'Tedarikçi bazında tam zamanında bağlantı onayı',
          'Dış kullanıcılara zorunlu oturum kaydı',
          'Her bağlantıda sponsora bildirim',
          'Tedarikçi bazında IP ve ağ kısıtları',
        ],
      },
      {
        title: 'Yerleşik yönetişim',
        description:
          'Tedarikçi kuruluşları birinci sınıf nesnelerdir: yetki devredilmiş tedarikçi yöneticileri kendi ekiplerini bir portal üzerinden yönetir, erişim gözden geçirmeleri kimin hâlâ erişime ihtiyacı olduğunu doğrular ve her işlem denetim izine düşer.',
        bullets: [
          'Yetki devredilmiş ekip yönetimli tedarikçi portalı',
          'Reddedilince devre dışı bırakan periyodik erişim gözden geçirmeleri',
          'Tedarikçi bazında denetim izi ve raporlama',
          'Portaldan yapılan davetler de sizin onayınızdan geçer',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Davet edin. Bağlanın. Süresi dolsun.',
    steps: [
      {
        title: 'Tedarikçiyi davet edin',
        description:
          'Tedarikçi kuruluşu, süre ve MFA gereksinimiyle bir davet talebi oluşturun. Onaylayıcılarınız doğrular; tedarikçi magic link ile kaydolur.',
      },
      {
        title: 'Oturumu aracılayın',
        description:
          'Tedarikçi, Monopam gateway üzerinden bağlanır: isterseniz onay kapılı, her zaman kayıtlı ve sponsoru anında bilgilendirilmiş olarak.',
      },
      {
        title: 'Süresi dolsun, gözden geçirin',
        description:
          'Pencere kapandığında erişim hem PAM hem kimlik katmanında biter. Erişim gözden geçirmeleri kimin hâlâ ihtiyacı olduğunu doğrular; ret hesabı devre dışı bırakır.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Dış ve iş ortağı erişimi',
        description: 'Daha geniş senaryo: federasyon, yükleniciler, yetki devri.',
        href: '/solutions/external-partners',
      },
      {
        label: 'OT tedarikçi erişimi',
        description: 'Rehber: fabrikalar ve OT ağları için güvenli uzaktan erişim.',
        href: '/learn/ot-vendor-access',
      },
      {
        label: 'NIS2 uyumu',
        description: 'Tedarik zinciri erişiminin Madde 21 önlemlerine eşlenmesi.',
        href: '/compliance/nis2',
      },
    ],
  },
}

type Props = { locale: string }

export default function VendorAccessPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/pam/vendor-access"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
